import { sourceLoopBars } from './previewHandoff';
import { sourceAudioDuration } from './sourceAudioDuration';

// The Booth -> Beat Arranger handoff. The song travels in the URL fragment
// (#ltbb_import=...), which browsers never send to a server, so lyrics go browser to
// browser only. Format matches Beat Arranger's existing ltbb_import reader plus a
// `booth` block (plan: Beat-Arranger .codex-handoff/booth-lyrics/plan.md, A1/A2/B5).

export const ARRANGER_URL = process.env.REACT_APP_BEAT_ARRANGER_URL || 'https://arranger.letthebeatbuild.me';
export const MAX_HANDOFF_URL = 24 * 1024;
const BOOTH_GROUPS = { Drums: 'Drums', Melodies: 'Melodies' };

// UTF-8 safe, URL-safe base64 without padding (same UTF-8 step as the library's handoff).
export const toUrlBase64 = (text) => btoa(unescape(encodeURIComponent(text)))
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export const fromUrlBase64 = (encoded) => {
  const padded = encoded.replace(/-/g, '+').replace(/_/g, '/')
    .padEnd(Math.ceil(encoded.length / 4) * 4, '=');
  return decodeURIComponent(escape(atob(padded)));
};

const clip = (value, max) => String(value || '').trim().slice(0, max);

// Only what Beat Arranger's schema keeps (title 80, style 400, 7 notes x 160, 8 sections x
// 16 lines x 140, 48 lines total). The Booth's own caps are already inside these.
export const boothBlock = (song = {}) => {
  let total = 0;
  const sections = (song.sections || []).slice(0, 8).map((section) => {
    const lines = (section.lines || []).slice(0, 16)
      .filter(() => { total += 1; return total <= 48; })
      .map((line) => ({ text: clip(line.text, 140) }));
    return { label: clip(section.label, 30), lines };
  });
  return {
    version: 1,
    title: clip(song.title, 80),
    vibe: song.vibe || null,
    style: clip(song.style, 400),
    cadence: (song.cadence || []).slice(0, 7)
      .map((item) => ({ label: clip(item.label, 20), note: clip(item.note, 160) })),
    sections,
  };
};

// loops: the chosen drums / melody loop objects from the pack (fresh preview URLs).
export const buildHandoffPayload = (song, bpm, loops = []) => ({
  tracks: loops.filter(Boolean).map((loop) => ({
    id: loop.loop_id,
    name: loop.title,
    url: loop.preview_url,
    bpm: loop.bpm,
    key: loop.key || '-',
    instrument_group: BOOTH_GROUPS[loop.instrument_group] || loop.instrument_group,
    bars: sourceLoopBars({ bars: loop.bars, bpm: loop.bpm, durationSeconds: loop.duration_seconds, instrumentGroup: loop.instrument_group }),
    style_tags: [],
  })),
  sessionBpm: bpm,
  sessionKey: loops.find((loop) => loop && loop.instrument_group === 'Melodies')?.key || null,
  knockEngine: null,
  source: 'booth',
  timestamp: Date.now(),
  booth: boothBlock(song),
});

export const buildHandoffUrl = (payload, base = ARRANGER_URL) => {
  const url = `${base.replace(/\/$/, '')}/#ltbb_import=${toUrlBase64(JSON.stringify(payload))}`;
  return url.length > MAX_HANDOFF_URL ? { tooLong: true } : { url };
};

// Beat Arranger's own phone rule: the shorter screen side under 600px.
export const isPhoneScreen = (screen = window.screen) => Math.min(screen.width, screen.height) < 600;

// Resolve only missing metadata, using fresh selected audio URLs. Refuse an
// unknown repeat period instead of silently handing the receiver four bars.
export const prepareHandoffLoops = async loops => Promise.all(loops.filter(Boolean).map(async loop => {
  const source = { ...loop };
  if (!(Number(source.bars) > 0) && !(Number(source.duration_seconds) > 0)) {
    source.duration_seconds = await sourceAudioDuration(source.preview_url);
  }
  const bars = sourceLoopBars({ bars: source.bars, bpm: source.bpm, durationSeconds: source.duration_seconds, instrumentGroup: source.instrument_group });
  if (!(bars > 0)) throw new Error('Check the selected loop’s tempo and bar length before sending.');
  return { ...source, bars };
}));
